/**
 * El documento que se está validando.
 *
 * ⚠️ Esta vista cargaba `document.file_path` —una columna que no escribía
 * nadie— y hacía `.split('.')` sobre `undefined`: TypeError al seleccionar el
 * primer documento de la cola, o sea que la pantalla del validador moría al
 * abrirse. Y apuntaba a `/api/v1/files/…`, una ruta que no existe.
 *
 * Desde el 02/09/2026 los ficheros SÍ se guardan, y se piden por la API con la
 * sesión del responsable: el navegador no puede cargarlos con un `<img src>` a
 * pelo porque la ruta exige el token, así que se descargan como Blob y se
 * muestran desde un object URL. Nunca se sirven como estático.
 */
import { useEffect, useState } from 'react';
import { getDocumentFile } from '../../../services/apiClient';
import { DocumentPreviewProps } from './types';

function fecha(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function DocumentPreview({ document: doc }: DocumentPreviewProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [ampliado, setAmpliado] = useState(false);

  useEffect(() => {
    setUrl(null);
    setError(null);
    if (!doc.file_uploaded) return;

    let vivo = true;
    let creado: string | null = null;

    getDocumentFile(doc.id)
      .then((blob) => {
        if (!vivo) return;
        creado = URL.createObjectURL(blob);
        setUrl(creado);
        setTipo(blob.type);
      })
      .catch(() => vivo && setError('No se pudo cargar el fichero'));

    return () => {
      vivo = false;
      // El object URL se revoca: si no, cada documento de la cola deja su
      // copia del fichero en memoria hasta que se cierre la pestaña.
      if (creado) URL.revokeObjectURL(creado);
    };
  }, [doc.id, doc.file_uploaded]);

  const esPdf = tipo === 'application/pdf';

  return (
    <div className="card-surface p-5 space-y-4">
      {doc.file_uploaded ? (
        <div
          className={`bg-ink rounded-xl overflow-hidden relative ${
            ampliado ? 'fixed inset-4 z-50' : 'h-[400px]'
          }`}
        >
          {error && (
            <div className="h-full grid place-items-center text-ink-3 text-[14px] px-6 text-center">
              {error}
            </div>
          )}
          {!error && !url && (
            <div className="h-full grid place-items-center text-ink-3 text-[14px]">
              Cargando…
            </div>
          )}
          {url && esPdf && (
            <iframe src={`${url}#toolbar=0`} className="w-full h-full" title="Documento" />
          )}
          {url && !esPdf && (
            <img src={url} alt="Documento" className="w-full h-full object-contain" />
          )}
          {url && (
            <button
              onClick={() => setAmpliado((v) => !v)}
              className="absolute top-2 right-2 h-9 px-3 rounded-lg bg-black/60 text-white text-[13px] font-semibold"
            >
              {ampliado ? 'Reducir' : 'Ampliar'}
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-surface-2 border border-dashed border-line-strong p-6 text-center">
          <p className="font-semibold text-[15px] text-ink-2">Sin fichero adjunto</p>
          <p className="text-[13.5px] text-ink-3 mt-1">
            La persona declaró el documento y su caducidad sin subir foto ni PDF:
            valídalo contra el papel que te enseñe.
          </p>
        </div>
      )}

      <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 text-[14px]">
        <dt className="text-ink-3">Trabajador</dt>
        <dd className="font-semibold">{doc.user_name}</dd>

        {doc.employee_code && (
          <>
            <dt className="text-ink-3">Código</dt>
            <dd className="font-mono">{doc.employee_code}</dd>
          </>
        )}

        <dt className="text-ink-3">Documento</dt>
        <dd className="font-semibold">{doc.document_type}</dd>

        <dt className="text-ink-3">Caducidad declarada</dt>
        <dd className="font-mono">{fecha(doc.expiry_date)}</dd>

        <dt className="text-ink-3">Registrado</dt>
        <dd className="font-mono">{fecha(doc.created_at)}</dd>
      </dl>

      {doc.is_blocking && (
        <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[13.5px]">
          Es un documento bloqueante: sin él vigente y validado, esta persona no
          puede fichar entrada.
        </p>
      )}
    </div>
  );
}
